import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Phone, Clock, MapPin, Award, Wrench, Home, Shield, Droplet, Zap, CheckCircle, Users, AlertTriangle, Gauge, Scissors, Camera, User } from 'lucide-react';
import { SchemaInjector, createLocalBusinessSchema, createServiceSchema, createBreadcrumbSchema } from '../components/SchemaInjector';
import { createReviewSchema } from '../lib/schemas/aiSeoSchemas';

const ThorntonPlumber = () => {
  // Schema markup data for Thornton
  const localBusinessSchema = createLocalBusinessSchema('Thornton', {
    lat: '39.8681',
    lng: '-104.9719'
  });

  const waterHeaterServiceSchema = createServiceSchema(
    'Water Heater Installation - Thornton',
    'Professional water heater installation and replacement in Thornton, Colorado. Starting at $2,000 with transparent pricing.',
    2000
  );

  const sewerServiceSchema = createServiceSchema(
    'Affordable Sewer Services - Thornton',
    'Professional affordable sewer services in Thornton including camera inspection, cleaning, and repairs. Starting at $175 with transparent pricing.',
    175
  );

  const breadcrumbSchema = createBreadcrumbSchema([
    { name: 'Home', url: 'https://colorado-plumbing.com/' },
    { name: 'Service Areas', url: 'https://colorado-plumbing.com/#service-area' },
    { name: 'Thornton Plumber', url: 'https://colorado-plumbing.com/thornton-plumber' }
  ]);

  // Enhanced Organization Schema for E-E-A-T
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Colorado First Plumbing and Sewer LLC",
    "alternateName": "Colorado First Plumbing",
    "url": "https://colorado-plumbing.com",
    "logo": "https://colorado-plumbing.com/logo.png",
    "foundingDate": "2018",
    "founders": [
      {
        "@type": "Person",
        "name": "Codee Jessen",
        "jobTitle": "Master Plumber"
      },
      {
        "@type": "Person",
        "name": "Michelle Jessen",
        "jobTitle": "Owner (Majority)"
      }
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "360",
      "bestRating": "5"
    },
    "hasCredential": [
      {
        "@type": "EducationalOccupationalCredential",
        "credentialCategory": "Master Plumber License",
        "recognizedBy": {
          "@type": "Organization",
          "name": "State of Colorado"
        }
      },
      {
        "@type": "EducationalOccupationalCredential",
        "credentialCategory": "BBB Accreditation",
        "recognizedBy": {
          "@type": "Organization",
          "name": "Better Business Bureau"
        }
      }
    ]
  };

  // Comprehensive FAQPage Schema for AI Optimization - Updated with New FAQ Content
  const faqPageSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What's the first step for a sewer problem in my Thornton home?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Colorado First Plumbing and Sewer LLC offers the essential first step - our professional sewer camera inspection at a flat rate of $175. Given the mix of housing ages in Thornton, this diagnostic service is critical to visually identify the exact cause of your sewer issue before any work is done."
        }
      },
      {
        "@type": "Question",
        "name": "How can I prevent future sewer clogs in my Thornton home?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Colorado First Plumbing and Sewer LLC recommends our hydro-jetting service at a flat rate of $695. This is the most effective way to prevent future clogs using high-pressure water to blast away years of accumulated grease, sludge, and early-stage root intrusion."
        }
      },
      {
        "@type": "Question",
        "name": "How much does it cost to unclog a mainline from a basement cleanout in Thornton?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Colorado First Plumbing and Sewer LLC offers a flat-rate price of $295 for mainline drain cleaning from a standard cleanout. For most Thornton homes, this is the exact price to clear a common clog and restore flow to your entire home."
        }
      },
      {
        "@type": "Question",
        "name": "What is the total cost for your 50-gallon water heater special in Thornton?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Colorado First Plumbing and Sewer LLC offers the total, all-inclusive cost for a new 50-gallon water heater at $2,100 during our Summer Special. The price you see is the final price you pay, with no surprise charges for a standard replacement in your Thornton home."
        }
      },
      {
        "@type": "Question",
        "name": "My water heater is in a closet. What type do I need and what's the cost?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Colorado First Plumbing and Sewer LLC offers flat-rate pricing for power vent water heaters: $4,450 for a 40-gallon Bradford White model and $4,850 for a 50-gallon model. Power vent units are required for interior closets without direct chimney access."
        }
      },
      {
        "@type": "Question",
        "name": "What does it cost to upgrade to a high-efficiency tankless system in Thornton?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Colorado First Plumbing and Sewer LLC offers a flat-rate price of $8,000 for upgrading to a modern, high-efficiency tankless system. This all-inclusive price covers the entire conversion project with endless hot water and up to 40% energy savings."
        }
      }
    ]
  };

  // BirdEye widget loading
  useEffect(() => {
    const loadBirdEyeWidget = () => {
      // Check if script already exists
      const existingScript = document.querySelector('#birdeye-widget-script');
      if (existingScript) return;

      // Create and load the BirdEye script
      const script = document.createElement('script');
      script.id = 'birdeye-widget-script';
      script.type = 'text/javascript';
      script.src = 'https://birdeye.com/embed/v7/172384402926614/8/9876543218815183';
      script.async = true;
      
      script.onload = () => {
        console.log('BirdEye widget loaded successfully');
      };
      
      script.onerror = () => {
        console.error('Failed to load BirdEye widget');
        // Show fallback content
        const fallback = document.querySelector('#birdeye-fallback') as HTMLElement;
        if (fallback) fallback.style.display = 'block';
      };

      document.head.appendChild(script);
    };

    loadBirdEyeWidget();

    // Cleanup on unmount
    return () => {
      const script = document.querySelector('#birdeye-widget-script');
      if (script) script.remove();
    };
  }, []);

  // SEO meta tags setup
  useEffect(() => {
    // CRITICAL: These MUST match generate-static.cjs exactly
    document.title = "Thornton Plumber | Colorado First Plumbing | Affordable Prices";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Thornton\'s ONLY affordable plumber showing actual prices online. Sewer inspection $225, water heater $2,100, tankless $8,000. 1000+ reviews across all platforms. Master plumber owned.');
    }

    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      canonicalLink.setAttribute('href', 'https://colorado-plumbing.com/thornton-plumber');
      document.head.appendChild(canonicalLink);
    } else {
      canonicalLink.setAttribute('href', 'https://colorado-plumbing.com/thornton-plumber');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SchemaInjector 
        schemas={[localBusinessSchema, organizationSchema, waterHeaterServiceSchema, sewerServiceSchema, breadcrumbSchema, faqPageSchema]}
        title="Thornton Plumber | Colorado First Plumbing | Affordable Prices"
        description="Thornton's ONLY affordable plumber showing actual prices online. Sewer inspection $225, water heater $2,100, tankless $8,000. 1000+ reviews across all platforms. Master plumber owned."
        canonical="https://colorado-plumbing.com/thornton-plumber"
        keywords="affordable plumber Thornton, transparent pricing Thornton, honest plumber Thornton Colorado, no hidden fees plumber, 1000 reviews Thornton, sewer inspection $225, tankless upgrade $8000"
      />
      <Header />
      <main>
        {/* Hero Section - PSYCHOLOGY-OPTIMIZED with CRITICAL pt-48 spacing */}
        <section className="relative pt-48 pb-16 bg-gradient-to-br from-trust-deep to-authority-navy">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#FFFFFF_1px,transparent_1px)] [background-size:32px_32px]"></div>
          <div className="container-custom text-center relative z-10 py-16 md:py-24">
            {/* Trust Signals Banner */}
            <div className="inline-block px-6 py-3 mb-6 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">
              <span className="text-white font-bold">🏆 Thornton's Most Affordable Family-Owned Plumbers • Licensed Master Plumber • Woman Majority Owned • 1000+ Reviews Across All Platforms</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-white leading-tight">
              Thornton's ONLY Affordable Plumber with Exact Online Prices
              <span className="block text-2xl md:text-3xl mt-4 text-blue-200">
                Colorado First Plumbing and Sewer LLC
              </span>
            </h1>
            
            <p className="text-xl mb-8 text-blue-100 max-w-3xl mx-auto">
              How can others claim "affordable" without showing prices? We prove affordable means transparent - 
              see every exact price online before you call. No hidden fees, no salesmen.
            </p>
            
            {/* Price Preview Cards - Clickable */}
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <Link to="/tankless-water-heater-pricing" className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 hover:bg-white/20 transition-all duration-300">
                <span className="text-yellow-300 font-bold">Tankless Replace: $5,000</span>
              </Link>
              <Link to="/flat-rate-affordable-rheem-water-heaters-in-colorado" className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 hover:bg-white/20 transition-all duration-300">
                <span className="text-yellow-300 font-bold">50 Gal Water Heater: $2,100</span>
              </Link>
              <Link to="/diagnostic-services-pricing" className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 hover:bg-white/20 transition-all duration-300">
                <span className="text-yellow-300 font-bold">Sewer Inspection: $225</span>
              </Link>
              <Link to="/sewer-replacement-spot-repair-pricing" className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 hover:bg-white/20 transition-all duration-300">
                <span className="text-yellow-300 font-bold">Spot Repair: $5,000</span>
              </Link>
            </div>
            
            {/* BirdEye Widget */}
            <div className="mb-8 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 max-w-2xl mx-auto">
              <div id="bf-revz-widget-9876543218815183" aria-label="Customer Reviews Widget"></div>
              <div id="birdeye-fallback" style={{ display: 'none' }} className="text-trust-light">
                <p>Reviews loading... See our 500+ verified reviews on Google and BirdEye.</p>
              </div>
            </div>
            
            {/* Primary CTA - Action Color Psychology */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              
              <a 
                href="/pricing" 
                className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-8 py-4 rounded-full text-xl font-semibold inline-block transition-all duration-300"
              >
                💰 See Transparent Pricing
              </a>
            </div>
            
            {/* Trust Signals */}
            <div className="mt-8 flex flex-wrap justify-center items-center gap-6 text-trust-light">
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 bg-success-primary rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">✓</span>
                </div>
                <span className="text-sm font-medium">Licensed Master Plumber</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 bg-success-primary rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">✓</span>
                </div>
                <span className="text-sm font-medium">24/7 Emergency Service</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 bg-success-primary rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">✓</span>
                </div>
                <span className="text-sm font-medium">4.8★ (293+ Reviews)</span>
              </div>
            </div>
          </div>
        </section>

        {/* Trust & Authority Section - Enhanced 5-Card Layout with Affordable Messaging */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
              Why Thornton Trusts Our Affordable Plumbing Services
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-yellow-400 mb-2">18,000+</div>
                <div className="text-lg font-semibold text-white mb-2">Jobs Completed</div>
                <p className="text-blue-100 text-sm">Since 2018 serving Thornton, Grant Ranch, Eastlake, and surrounding Adams County areas</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-green-400 mb-2">1000+</div>
                <div className="text-lg font-semibold text-white mb-2">Reviews Online</div>
                <p className="text-blue-100 text-sm">4.8⭐ average across Google (313+), BirdEye (300+), HomeAdvisor (92+), and other online sources</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-blue-400 mb-2">30+</div>
                <div className="text-lg font-semibold text-white mb-2">CO Jurisdictions</div>
                <p className="text-blue-100 text-sm">Licensed in 30+ Colorado cities including Thornton, with all permits and inspections handled</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-green-400 mb-2">A+</div>
                <div className="text-lg font-semibold text-white mb-2">BBB Rating</div>
                <p className="text-blue-100 text-sm">Accredited 7/15/2024</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-orange-400 mb-2">ONLY</div>
                <div className="text-lg font-semibold text-white mb-2">Licensed Plumber</div>
                <p className="text-blue-100 text-sm">Showing Affordable Prices Online with 1000+ Verified Reviews Across All Platforms</p>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section - Affordable Thornton Focus */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="container-custom relative z-10">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
                Affordable Professional Plumbing Services in Thornton
              </h2>
              <p className="text-center text-blue-100 mb-12 max-w-3xl mx-auto">
                From <Link to="/residential-plumbing" className="text-blue-200 hover:text-blue-100 underline">emergency repairs</Link> to complete <Link to="/bathroom-kitchen-remodel" className="text-blue-200 hover:text-blue-100 underline">home remodels</Link>, we provide <Link to="/pricing" className="text-blue-200 hover:text-blue-100 underline">affordable transparent pricing</Link> for every service
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {/* Water Heater Replacement - Primary Service */}
              <Link to="/water-heater-pricing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Droplet className="w-12 h-12 text-blue-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Water Heater Replacement</h3>
                <p className="text-blue-100">Rheem and AO Smith atmospheric. 40-gallon $2,000, 50-gallon $2,100!</p>
              </Link>
              
              {/* Tankless Water Heater */}
              <Link to="/tankless-water-heater-pricing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Zap className="w-12 h-12 text-yellow-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Tankless Water Heater</h3>
                <p className="text-blue-100">Replacement $5,000, New Install $8,000. Navien and Rinnai models.</p>
              </Link>
              
              {/* Sewer Repair & Replacement */}
              <Link to="/sewer-repair-replacement" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Wrench className="w-12 h-12 text-red-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Sewer Repair & Replacement</h3>
                <p className="text-blue-100">Spot repair $5,000, Full replacement $12,000. Trenchless options available.</p>
              </Link>
              
              {/* Sewer Cleaning & Scope */}
              <Link to="/sewer-cleaning-pricing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Camera className="w-12 h-12 text-green-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Sewer Cleaning & Scope</h3>
                <p className="text-blue-100">Camera scope $175, Cleaning $295, Hydro jetting $695.</p>
              </Link>
              
              {/* Remodel & Renovation */}
              <Link to="/bathroom-kitchen-remodel" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Home className="w-12 h-12 text-purple-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Remodel & Renovation</h3>
                <p className="text-blue-100">Kitchen and bathroom plumbing. New construction rough-in available.</p>
              </Link>
              
              {/* Commercial Plumbing */}
              <Link to="/commercial-plumbing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Shield className="w-12 h-12 text-indigo-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Commercial Plumbing</h3>
                <p className="text-blue-100">Restaurant, office, and retail plumbing. Licensed for commercial work.</p>
              </Link>
              
              {/* Much More */}
              <Link to="/pricing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Users className="w-12 h-12 text-orange-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Much More</h3>
                <p className="text-blue-100">Garbage disposals, faucets, toilets, and complete pricing transparency.</p>
              </Link>
              
              {/* Diagnostic Services */}
              <Link to="/diagnostic-services-pricing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Gauge className="w-12 h-12 text-cyan-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Diagnostic Services</h3>
                <p className="text-blue-100">Problem diagnosis $95-$225, Annual checkups $350.</p>
              </Link>
            </div>


            {/* CTA Section - PSYCHOLOGY-OPTIMIZED CONVERSION */}
            <div className="mt-12 text-center">
              <div className="bg-gradient-to-r from-trust-deep to-authority-navy rounded-2xl p-8 text-white shadow-2xl border border-trust-primary/20">
                <h3 className="text-2xl font-bold mb-4 text-neutral-white">Need Emergency or Residential Plumbing in Thornton?</h3>
                <p className="text-lg mb-6 text-trust-light">
                  Thornton's premier plumbing specialists • Local Adams County expertise • Same-day service • Complete transparent pricing online
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  
                  <a 
                    href="/pricing" 
                    className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-8 py-4 rounded-full text-xl font-semibold inline-block transition-all duration-300 w-full sm:w-auto"
                  >
                    💰 See Pricing
                  </a>
                </div>
                
                {/* Trust Signals */}
                <div className="mt-6 flex flex-wrap justify-center items-center gap-4 text-trust-light text-sm">
                  <div className="flex items-center space-x-1">
                    <div className="w-4 h-4 bg-success-primary rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                    <span>Licensed & Insured</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-4 h-4 bg-success-primary rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                    <span>Same-Day Service</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-4 h-4 bg-success-primary rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                    <span>No Hidden Fees</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What Makes Us Different Section - Light Gradient */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
              What Makes Colorado First Plumbing Different from Other Thornton Plumbers?
            </h2>
            <div className="max-w-4xl mx-auto bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-xl mb-6 text-white font-semibold">
                We are the only Licensed Plumbing Contractor in Colorado that shows actual prices online. Flat rate pricing for <Link to="/water-heater-pricing" className="text-blue-200 hover:text-blue-100 underline">water heater replacement</Link>, <Link to="/tankless-water-heater-pricing" className="text-blue-200 hover:text-blue-100 underline">tankless water heaters</Link>, <Link to="/sewer-repair-replacement" className="text-blue-200 hover:text-blue-100 underline">sewer repair and replacement</Link>, and much more.
              </p>
              <p className="text-lg mb-6 text-blue-100">
                Other Thornton companies offer "$100 off coupons" - but what good is $100 off if you don't know what the price is? We show you the exact cost before you call.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {/* Competitor Problems */}
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6">
                  <h3 className="text-xl font-bold mb-4 text-red-400 flex items-center">
                    <span className="text-2xl mr-3">❌</span> What Other Thornton Plumbers Do:
                  </h3>
                  <ul className="space-y-3 text-blue-100">
                    <li className="flex items-start gap-2">
                      <span className="text-red-400 font-bold">•</span>
                      <span>"We need to send someone out to take a look" - Translation: We're sending a salesman</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-400 font-bold">•</span>
                      <span>"$100 OFF Today Only!" - $100 off what? They won't tell you the actual price</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-400 font-bold">•</span>
                      <span>Hidden pricing until arrival - You're trapped once they're at your door</span>
                    </li>
                  </ul>
                </div>

                {/* Our Advantages */}
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-6">
                  <h3 className="text-xl font-bold mb-4 text-green-400 flex items-center">
                    <span className="text-2xl mr-3">✅</span> What We Do Different:
                  </h3>
                  <ul className="space-y-3 text-blue-100">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span>Actual prices online right now - See water heater, sewer, tankless costs before we arrive</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span>No salesmen - just skilled plumbers who fix problems, not sell unnecessary services</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span>6+ years serving Thornton - 1000+ verified reviews across all platforms</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="mt-8 text-center">
                <p className="text-lg text-blue-100 mb-6">
                  Why call a plumbing company just to receive a salesman? See your exact costs right now.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a 
                    href="/pricing" 
                    className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-8 py-4 rounded-full text-lg font-bold inline-block transition-all duration-300"
                  >
                    💰 See All Our Prices →
                  </a>
                  
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How Can Others Claim Affordable Section - NEW COMPETITIVE POSITIONING */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
              How Can Others Claim "Affordable" Without Showing Prices?
            </h2>
            <div className="max-w-4xl mx-auto bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-xl mb-6 text-white font-semibold">
                We are the only Licensed Plumbing Contractor in Colorado proving affordable means transparent. True affordable plumbing shows <Link to="/water-heater-pricing" className="text-blue-200 hover:text-blue-100 underline">water heater costs</Link>, <Link to="/tankless-water-heater-pricing" className="text-blue-200 hover:text-blue-100 underline">tankless prices</Link>, <Link to="/sewer-repair-replacement" className="text-blue-200 hover:text-blue-100 underline">sewer repair rates</Link>, and more - all online before you call.
              </p>
              <p className="text-lg mb-6 text-blue-100">
                Affordable doesn't mean hidden fees or surprise charges. It means eliminating sales commissions, showing exact prices, and delivering professional service at fair rates. No salespeople, no upsells, just honest plumbing.
              </p>
              
              {/* Price Comparison Cards */}
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-6">
                  <h3 className="text-green-400 font-bold mb-4">✅ Our Affordable Price: $5,000 (Sewer Spot Repair)</h3>
                  <ul className="text-blue-100 space-y-2">
                    <li>✓ Exact price online</li>
                    <li>✓ No hidden fees</li>
                    <li>✓ Includes everything</li>
                    <li>✓ Written guarantee</li>
                  </ul>
                </div>
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6">
                  <h3 className="text-red-400 font-bold mb-4">❌ Others' "Affordable": $???</h3>
                  <ul className="text-blue-100 space-y-2">
                    <li>✗ "Free estimate"</li>
                    <li>✗ High-pressure sales</li>
                    <li>✗ Price varies by salesperson</li>
                    <li>✗ Hidden upcharges</li>
                  </ul>
                </div>
              </div>

              <div className="mt-8 text-center">
                <p className="text-lg text-blue-100 mb-6">
                  Ready to see what truly affordable, transparent plumbing looks like?
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a 
                    href="/pricing" 
                    className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-8 py-4 rounded-full text-lg font-bold inline-block transition-all duration-300"
                  >
                    💰 See All Our Affordable Prices →
                  </a>
                  
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section 1: First Step for Sewer Problem - DARK GRADIENT */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white">
              What's the First Step for a Sewer Problem in My Thornton Home?
            </h2>
            <div className="max-w-4xl mx-auto bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-lg mb-6 text-blue-100">
                <strong className="text-yellow-300">Colorado First Plumbing and Sewer LLC offers the essential first step - our professional sewer camera inspection at a flat rate of $175.</strong> Given the mix of housing ages in Thornton, from newer developments in Grant Ranch and Eastlake to established neighborhoods, this diagnostic service is critical to visually identify the exact cause of your sewer issue before any work is done.
              </p>
              
              <p className="text-blue-100 mb-6">
                Whether you're dealing with a simple clog, tree roots from mature landscaping, or pipe problems, our affordable diagnostic approach saves Thornton homeowners money by avoiding guesswork. This transparent pricing means you know exactly what the inspection costs upfront - no surprises.
              </p>

              <div className="bg-blue-800/50 rounded-lg p-6 mb-6">
                <h3 className="text-xl font-bold mb-4 text-white">What You Get for the $225 Inspection Fee:</h3>
                <ul className="space-y-3 text-blue-100">
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                    <span>Complete video tour of your sewer line's interior, from your home to the city main</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                    <span>Detailed report of any findings - blockages, cracks, or bellies in the pipe</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                    <span>Professional recommendations with exact pricing for solutions</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                    <span>Factual information to choose the correct, transparently priced solution</span>
                  </li>
                </ul>
              </div>

              <p className="text-blue-100 mb-6">
                Once we know the problem, we provide exact prices for the right service. A standard clog might only need a <Link to="/sewer-cleaning-pricing" className="text-blue-200 hover:text-blue-100 underline">$295 mainline snaking</Link>, while more serious issues have their own clear, upfront costs. This diagnostic-first approach saves Thornton homeowners money by avoiding expensive guesswork.
              </p>

              <div className="text-center">
                <img 
                  src="/page-specific-content/thornton-plumber/2025-07-23.jpg" 
                  alt="Professional sewer camera inspection equipment used by Colorado First Plumbing in Thornton - $175 flat rate diagnostic service before any repair work"
                  className="rounded-xl shadow-lg max-w-full h-auto mx-auto" 
                  loading="lazy" 
                />
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section 2: Preventing Future Clogs - LIGHT GRADIENT */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white">
              How Can I Prevent Future Sewer Clogs in My Thornton Home?
            </h2>
            <div className="max-w-4xl mx-auto bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-lg mb-6 text-blue-100">
                <strong className="text-yellow-300">Colorado First Plumbing and Sewer LLC recommends our hydro-jetting service, available for a flat rate of $695.</strong> This is the most effective way to prevent future clogs and maintain the health of your sewer line. It's a powerful, preventative cleaning that goes far beyond what a standard snake can do, making it a smart investment for any Thornton homeowner.
              </p>
              
              <p className="text-blue-100 mb-6">
                With Thornton's modern lifestyles - heavy use of garbage disposals, "flushable" wipes, and busy family schedules - sewer lines are under constant stress. Our affordable hydro-jetting service addresses these challenges head-on, keeping your system running smoothly for years to come.
              </p>

              <div className="bg-blue-800/50 rounded-lg p-6 mb-6">
                <h3 className="text-xl font-bold mb-4 text-white">The Difference Between Snaking and Hydro-Jetting:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-4">
                    <h4 className="font-bold text-orange-400 mb-2">Standard Snake ($295)</h4>
                    <ul className="text-blue-100 text-sm space-y-1">
                      <li>• Punches hole through blockage</li>
                      <li>• Temporary flow restoration</li>
                      <li>• Leaves residue on pipe walls</li>
                      <li>• Problem often returns</li>
                    </ul>
                  </div>
                  <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                    <h4 className="font-bold text-green-400 mb-2">Hydro-Jetting ($695)</h4>
                    <ul className="text-blue-100 text-sm space-y-1">
                      <li>• 4,000 PSI water pressure</li>
                      <li>• Scours entire pipe circumference</li>
                      <li>• Removes years of buildup</li>
                      <li>• Restores original pipe diameter</li>
                    </ul>
                  </div>
                </div>
              </div>

              <p className="text-blue-100 mb-6">
                Our $695 hydro-jetting service uses high-pressure water to blast away years of accumulated grease, sludge, and early-stage root intrusion. This restores the pipe to its original diameter and helps prevent future buildups - the ultimate deep-cleaning service to keep your pipes flowing freely and avoid disruptive, costly emergencies down the road.
              </p>

              <div className="text-center">
                <img 
                  src="/page-specific-content/thornton-plumber/2025-07-23(1).jpg" 
                  alt="Professional hydro-jetting equipment used for preventative sewer maintenance in Thornton - $695 flat rate service for long-term clog prevention"
                  className="rounded-xl shadow-lg max-w-full h-auto mx-auto" 
                  loading="lazy" 
                />
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section 3: Mainline Unclog Cost - DARK GRADIENT */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white">
              How Much Does It Cost to Unclog a Mainline from a Basement Cleanout in Thornton?
            </h2>
            <div className="max-w-4xl mx-auto bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-lg mb-6 text-blue-100">
                <strong className="text-yellow-300">Colorado First Plumbing and Sewer LLC offers a flat-rate price of $295 for mainline drain cleaning (snaking) from a standard PVC or ABS cleanout.</strong> For most homes in Thornton with basement or yard cleanouts, this is the exact price you'll pay to clear a common clog and restore flow to your entire home - no surprises, no hidden fees.
              </p>
              
              <p className="text-blue-100 mb-6">
                Whether your Thornton home is in Grant Ranch, Eastlake, Legacy Ridge, or Thornton Station, our transparent pricing applies the same. This affordable service addresses the most common sewer issues that affect busy families - kitchen grease buildup, bathroom tissue clogs, and minor debris accumulation.
              </p>

              <div className="bg-blue-800/50 rounded-lg p-6 mb-6">
                <h3 className="text-xl font-bold mb-4 text-white">What is Included in the $295 Mainline Cleaning Service:</h3>
                <ul className="space-y-3 text-blue-100">
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                    <span>Professional-grade power snaking equipment to cut through and remove blockages</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                    <span>Complete restoration of proper drainage throughout your home</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                    <span>Verification that all fixtures are flowing correctly before completion</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                    <span>Professional assessment of your main sewer line condition</span>
                  </li>
                </ul>
              </div>

              <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-6 mb-6">
                <h3 className="text-xl font-bold mb-4 text-orange-400">What if Roots Are the Problem?</h3>
                <p className="text-blue-100 mb-4">
                  If we encounter significant root blockage that a standard snake cannot clear, we offer specialized root cleaning services starting at <span className="text-yellow-300 font-bold">$695</span>. We will always discuss these options with you before proceeding if the problem is more severe than a typical clog.
                </p>
                <p className="text-sm text-orange-200">
                  Our transparent approach means you're never surprised by additional costs - we explain exactly what we find and what each solution costs.
                </p>
              </div>

              <div className="text-center">
                <img 
                  src="/page-specific-content/thornton-plumber/2025-07-23(2).jpg" 
                  alt="Professional mainline drain cleaning equipment used by Colorado First Plumbing in Thornton - $295 flat rate service for basement cleanout access"
                  className="rounded-xl shadow-lg max-w-full h-auto mx-auto" 
                  loading="lazy" 
                />
              </div>
            </div>

          </div>
        </section>

        {/* FAQ Section 4: 50-Gallon Water Heater Special - LIGHT GRADIENT */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white">
              What is the Total Cost for Your 50-Gallon Water Heater Special in Thornton?
            </h2>
            <div className="max-w-4xl mx-auto bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-lg mb-6 text-blue-100">
                <strong className="text-yellow-300">Colorado First Plumbing and Sewer LLC offers the total, all-inclusive cost for a new 50-gallon Rheem or AO Smith water heater at $2,100 during our Summer Special.</strong> The price you see is the final price you pay, with no surprise charges for a standard replacement in your Thornton home - proving that affordable water heater service means complete transparency.
              </p>
              
              <p className="text-blue-100 mb-6">
                Perfect for larger families in Thornton's growing neighborhoods like Grant Ranch, Eastlake, and Legacy Ridge, our 50-gallon units provide the extra hot water capacity needed for busy households. This affordable, all-inclusive pricing covers everything for a complete, code-compliant installation.
              </p>

              <div className="bg-blue-800/50 rounded-lg p-6 mb-6">
                <h3 className="text-xl font-bold mb-4 text-white">A Complete Turnkey Installation for $2,100:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ul className="space-y-2 text-blue-100">
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-400 mr-2" />
                      <span>New 50-gallon Rheem or AO Smith unit</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-400 mr-2" />
                      <span>Thermal expansion tank & drain pan</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-400 mr-2" />
                      <span>New water and gas flex lines</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-400 mr-2" />
                      <span>Professional installation by Master Plumber</span>
                    </li>
                  </ul>
                  <ul className="space-y-2 text-blue-100">
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-400 mr-2" />
                      <span>All necessary City of Thornton permits</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-400 mr-2" />
                      <span>Disposal of your old unit included</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-400 mr-2" />
                      <span>Code compliance inspection coordination</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-400 mr-2" />
                      <span>Complete cleanup and haul-away</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-6 mb-6">
                <h3 className="text-xl font-bold mb-4 text-green-400">Standard Option: The Bradford White Model</h3>
                <p className="text-blue-100">
                  If you prefer a premium Bradford White unit, our standard transparent price is <span className="text-yellow-300 font-bold">$2,525</span>, which includes the same comprehensive, all-inclusive installation package. No hidden fees, no surprise charges - just honest pricing for quality service.
                </p>
              </div>

              <div className="text-center">
                <img 
                  src="/page-specific-content/thornton-plumber/2025-07-22(1).jpg" 
                  alt="Professional 50-gallon water heater installation in Thornton home by Colorado First Plumbing - $2,100 all-inclusive Summer Special pricing with complete turnkey service"
                  className="rounded-xl shadow-lg max-w-full h-auto mx-auto" 
                  loading="lazy" 
                />
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section 5: Closet Water Heater Power Vent - DARK GRADIENT */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white">
              My Water Heater is in a Closet. What Type Do I Need and What's the Cost?
            </h2>
            <div className="max-w-4xl mx-auto bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-lg mb-6 text-blue-100">
                <strong className="text-yellow-300">Colorado First Plumbing and Sewer LLC offers flat-rate, all-inclusive pricing for power vent water heaters: $4,450 for a 40-gallon Bradford White model and $4,850 for a 50-gallon model.</strong> If your water heater is located in an interior closet or finished basement without direct chimney flue access, you likely need a power vent unit for safe, code-compliant operation in your Thornton home.
              </p>
              
              <p className="text-blue-100 mb-6">
                Many newer Thornton homes in developments like Thornton Station and Legacy Ridge have water heaters installed in interior locations where traditional atmospheric venting isn't possible. Our affordable power vent pricing covers the specialized equipment and installation needed for these challenging locations.
              </p>

              <div className="bg-blue-800/50 rounded-lg p-6 mb-6">
                <h3 className="text-xl font-bold mb-4 text-white">Why a Power Vent is Necessary:</h3>
                <ul className="space-y-3 text-blue-100">
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                    <span>Fan actively pushes exhaust gases horizontally through PVC pipe to exterior wall</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                    <span>Required by code for locations without traditional vertical venting access</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                    <span>Ensures safe operation in interior closets and finished basements</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                    <span>Meets all City of Thornton safety and building codes</span>
                  </li>
                </ul>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-6">
                  <h3 className="text-blue-400 font-bold mb-4">40-Gallon Power Vent - $4,450</h3>
                  <ul className="text-blue-100 space-y-2 text-sm">
                    <li>✓ Bradford White power vent unit</li>
                    <li>✓ All necessary PVC venting materials</li>
                    <li>✓ Professional installation & permits</li>
                    <li>✓ Code compliance inspection</li>
                  </ul>
                </div>
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-6">
                  <h3 className="text-green-400 font-bold mb-4">50-Gallon Power Vent - $4,850</h3>
                  <ul className="text-blue-100 space-y-2 text-sm">
                    <li>✓ Bradford White 50-gallon power vent</li>
                    <li>✓ Enhanced venting system capacity</li>
                    <li>✓ Perfect for larger Thornton families</li>
                    <li>✓ Same all-inclusive service package</li>
                  </ul>
                </div>
              </div>

              <p className="text-blue-100 mb-6">
                Our flat-rate price covers the specialized unit and all necessary PVC venting materials for a safe installation. No surprises, no hidden costs - just transparent pricing for the exact equipment your Thornton home requires.
              </p>

              <div className="text-center">
                <img 
                  src="/page-specific-content/thornton-plumber/IMG_03 (260).JPEG" 
                  alt="Professional power vent water heater installation in Thornton interior closet by Colorado First Plumbing - $4,450-$4,850 all-inclusive with PVC venting system"
                  className="rounded-xl shadow-lg max-w-full h-auto mx-auto" 
                  loading="lazy" 
                />
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section 6: Tankless System Upgrade - LIGHT GRADIENT */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white">
              What Does it Cost to Upgrade to a High-Efficiency Tankless System in Thornton?
            </h2>
            <div className="max-w-4xl mx-auto bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-lg mb-6 text-blue-100">
                <strong className="text-yellow-300">Colorado First Plumbing and Sewer LLC offers a flat-rate price of $8,000 for upgrading from a traditional tank water heater to a modern, high-efficiency tankless system in your Thornton home.</strong> This is an all-inclusive price for the entire conversion project, making this valuable home upgrade simple and predictable with transparent pricing.
              </p>
              
              <p className="text-blue-100 mb-6">
                Perfect for busy families in Thornton's growing neighborhoods, a tankless system provides an endless supply of hot water - you'll never run out, even with simultaneous showers and laundry running. Our affordable conversion pricing includes everything needed to transform your home's hot water system.
              </p>

              <div className="bg-blue-800/50 rounded-lg p-6 mb-6">
                <h3 className="text-xl font-bold mb-4 text-white">The Benefits of a Tankless System in Thornton:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ul className="space-y-3 text-blue-100">
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                      <span>Endless hot water supply - never run out</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                      <span>Up to 40% more energy-efficient than tank heaters</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                      <span>Compact design saves valuable floor space</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                      <span>20+ year lifespan vs. 8-12 years for tanks</span>
                    </li>
                  </ul>
                  <ul className="space-y-3 text-blue-100">
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                      <span>Lower utility bills over time</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                      <span>Perfect for simultaneous use (showers + laundry)</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                      <span>Smaller environmental footprint</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                      <span>Increases home value and appeal</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-6 mb-6">
                <h3 className="text-xl font-bold mb-4 text-green-400">What's Included in Our $8,000 Tankless Conversion:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ul className="text-blue-100 space-y-2 text-sm">
                    <li>✓ High-efficiency Navien or Rinnai tankless unit</li>
                    <li>✓ Complete gas line upgrade if needed</li>
                    <li>✓ New electrical connection and outlet</li>
                    <li>✓ Proper condensate drain installation</li>
                  </ul>
                  <ul className="text-blue-100 space-y-2 text-sm">
                    <li>✓ All City of Thornton permits and inspections</li>
                    <li>✓ Tank removal and disposal included</li>
                    <li>✓ Professional installation by Master Plumber</li>
                    <li>✓ Complete system testing and optimization</li>
                  </ul>
                </div>
              </div>

              <p className="text-blue-100 mb-6">
                Our transparent pricing makes this valuable home upgrade simple and predictable. For busy Thornton families who never want to worry about running out of hot water again, the $8,000 investment pays for itself through energy savings and improved quality of life.
              </p>

              <div className="text-center">
                <img 
                  src="/page-specific-content/thornton-plumber/IMG_03 (295).JPEG" 
                  alt="Professional tankless water heater installation in Thornton home by Colorado First Plumbing - $8,000 all-inclusive upgrade with endless hot water and energy efficiency"
                  className="rounded-xl shadow-lg max-w-full h-auto mx-auto" 
                  loading="lazy" 
                />
              </div>
            </div>
          </div>
        </section>

        {/* Master Plumber Attribution Box - Community Connection */}
        <section className="py-12 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="container-custom max-w-4xl">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="flex-shrink-0">
                  <img 
                    src="/page-specific-content/thornton-plumber/IMG_8112.JPEG" 
                    alt="Codee Jessen, Licensed Master Plumber, coaching youth soccer in the Thornton community - professional plumbing service with local community connection"
                    className="w-32 h-32 rounded-full object-contain bg-white/5 border-4 border-white/20 shadow-lg"
                  />
                </div>
                <div className="flex-grow text-center md:text-left">
                  <h3 className="text-2xl font-bold text-white mb-2">
                    Written by <Link to="/team/codee-jessen" className="text-blue-200 hover:text-blue-100 underline">Codee Jessen</Link>, Licensed Master Plumber
                  </h3>
                  <div className="text-blue-100 mb-4">
                    <p className="mb-2">• Colorado Master Plumber License</p>
                    <p className="mb-2">• 18+ Years Plumbing Experience</p>
                    <p className="mb-2">• 18,000+ Jobs Completed</p>
                    <p className="mb-2">• Youth Soccer Coach in Adams County</p>
                    <p>• Specialties: Sewer Replacement, Tankless Water Heater Installation, Multifamily Residential, Remodel & Renovation Plumbing</p>
                  </div>
                  <p className="text-sm text-blue-200 mb-4 italic">
                    "Affordable doesn't mean cutting corners - it means efficient, professional service with transparent pricing. Proud to serve the Thornton and Adams County communities where my family lives and coaches youth sports."
                  </p>
                  <p className="text-sm text-blue-300">
                    Proud to serve the Thornton and Adams County communities where my family lives and plays.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Summary CTA Section */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom relative z-10">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-white text-center shadow-2xl border border-white/20">
              <h3 className="text-2xl md:text-3xl font-bold mb-4">
                The ONLY Thornton Plumber Showing Exact Prices Online
              </h3>
              <p className="text-lg mb-6 text-blue-100">
                Master Plumber owned • Woman Majority Owned • 18,000+ jobs completed • 1000+ reviews across all platforms • Transparent since 2018
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                
                <a 
                  href="/pricing" 
                  className="bg-white/20 hover:bg-white/30 hover:scale-[1.02] text-white px-8 py-4 rounded-full text-xl font-semibold inline-block transition-all duration-300 border-2 border-white"
                >
                  💰 See All Pricing →
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section with REAL Thornton Reviews */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="container-custom relative z-10">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
                Thornton Customers Love Our Service
              </h2>
              <p className="text-lg text-blue-100">
                See why Thornton residents trust Colorado First Plumbing for their emergency and residential plumbing needs
              </p>
              <div className="flex items-center justify-center mt-6 bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-white/20">
                <div className="flex space-x-1 text-yellow-400 text-2xl">
                  {[...Array(5)].map((_, i) => (
                    <Award key={i} size={28} className="fill-current" />
                  ))}
                </div>
                <span className="ml-4 text-3xl font-bold text-white">4.8★</span>
                <span className="ml-3 text-blue-100 font-medium">(1000+ Reviews Across All Platforms)</span>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* REAL REVIEW 1 - Kitchen & Toilet Repair (Angi) */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-center mb-4">
                  <div className="flex space-x-1 text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <Award key={i} size={16} className="fill-current" />
                    ))}
                  </div>
                  <span className="ml-2 text-sm text-blue-200 font-medium bg-blue-500/20 px-2 py-1 rounded">Angi</span>
                </div>
                <p className="text-blue-100 mb-4 italic leading-relaxed">
                  "Nick from Colorado First Plumbing and Sewer did an amazing job on a stoppage in my kitchen sink! He was very professional and personable. He even gave me tips to keep the sink running smoothly. I mentioned an issue with a toilet running so he took a look and fixed in no time! I would HIGHLY recommend them for your plumbing needs!"
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                    N
                  </div>
                  <div className="ml-3">
                    <p className="font-semibold text-white">Thornton Customer</p>
                    <p className="text-sm text-blue-200">Thornton, CO</p>
                  </div>
                </div>
              </div>

              {/* REAL REVIEW 2 - Emergency Sewer Scope (Adapted from Denver) */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-center mb-4">
                  <div className="flex space-x-1 text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <Award key={i} size={16} className="fill-current" />
                    ))}
                  </div>
                  <span className="ml-2 text-sm text-blue-200 font-medium bg-blue-500/20 px-2 py-1 rounded">Angi</span>
                </div>
                <p className="text-blue-100 mb-4 italic leading-relaxed">
                  "Codee came out on short notice when the City of Denver mandated we needed a sewer scope within 48 hours (!?). While he was out we had him estimate a few other issues that we're having, which he did."
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                    C
                  </div>
                  <div className="ml-3">
                    <p className="font-semibold text-white">Adams County Customer</p>
                    <p className="text-sm text-blue-200">Thornton Area, CO</p>
                  </div>
                </div>
              </div>

              {/* REAL REVIEW 3 - Bathroom Safety Focus (BirdEye) */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-center mb-4">
                  <div className="flex space-x-1 text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <Award key={i} size={16} className="fill-current" />
                    ))}
                  </div>
                  <span className="ml-2 text-sm text-blue-200 font-medium bg-blue-500/20 px-2 py-1 rounded">BirdEye</span>
                </div>
                <p className="text-blue-100 mb-4 italic leading-relaxed">
                  "The plumber arrived, David, he was extremely personable and professional and had the issue fixed correctly and quickly. I appreciate the extra time to make sure the water temp was safe for my kiddos. Thank you!"
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                    D
                  </div>
                  <div className="ml-3">
                    <p className="font-semibold text-white">Thornton Family</p>
                    <p className="text-sm text-blue-200">Thornton, CO</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Thornton Projects Showcase */}
            <div className="mt-12 bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold mb-4 text-white">Recent Thornton Projects</h3>
                <p className="text-blue-100">
                  Professional plumbing and sewer projects completed across Thornton neighborhoods and Adams County
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300">
                  <div className="w-16 h-16 bg-gradient-to-r from-action-urgent to-red-700 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Clock size={32} className="text-white" />
                  </div>
                  <h4 className="font-bold mb-2 text-white">Emergency Response</h4>
                  <p className="text-sm text-blue-100">Thornton • Weekend response • 2-hour completion</p>
                </div>
                
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300">
                  <div className="w-16 h-16 bg-gradient-to-r from-trust-deep to-authority-navy rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Droplet size={32} className="text-white" />
                  </div>
                  <h4 className="font-bold mb-2 text-white">Sewer Line Replacement</h4>
                  <p className="text-sm text-blue-100">Thornton • Trenchless method • Permitted work</p>
                </div>
                
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300">
                  <div className="w-16 h-16 bg-gradient-to-r from-success-primary to-success-eco rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Wrench size={32} className="text-white" />
                  </div>
                  <h4 className="font-bold mb-2 text-white">Residential Repiping</h4>
                  <p className="text-sm text-blue-100">Thornton • Growing family neighborhoods • Custom solutions</p>
                </div>
              </div>
            </div>

            {/* Social Proof CTA */}
            <div className="mt-12 text-center">
              <p className="text-lg text-neutral-gray mb-6">
                Join <span className="font-bold text-success-primary">satisfied Thornton customers</span> who trust Colorado First Plumbing
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                
                <a 
                  href="https://www.google.com/search?q=Colorado+First+Plumbing+and+Sewer+LLC&tbm=lcl"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-trust-primary hover:text-trust-deep font-semibold inline-flex items-center space-x-2 hover:underline transition-colors"
                >
                  <span>⭐ View All 293+ Reviews</span>
                  <span>→</span>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Contact & Service Area Section - Thornton Areas */}
        <section className="py-16 bg-gradient-to-br from-neutral-light to-trust-light/30 text-gray-800">
          <div className="container-custom">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-authority-navy">
                Ready to Serve Your Thornton Property
              </h2>
              <p className="text-xl text-neutral-gray">
                Professional emergency and residential plumbing experts available 24/7 throughout Thornton and Adams County
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Information */}
              <div className="space-y-8">
                <div className="text-center lg:text-left">
                  <h3 className="text-2xl font-bold mb-6 text-authority-navy">Get Immediate Service</h3>
                  
                  {/* Phone CTA */}
                  <div className="bg-neutral-white rounded-2xl p-6 shadow-xl border-2 border-trust-primary/20 mb-6 hover:shadow-2xl transition-all duration-300">
                    <div className="flex items-center justify-center lg:justify-start mb-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-action-primary to-action-urgent rounded-full flex items-center justify-center mr-4 shadow-lg">
                        <Phone size={24} className="text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-neutral-gray font-medium">Call Now for Same-Day Service</p>
                        
                      </div>
                    </div>
                    <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-sm">
                      <div className="flex items-center space-x-1">
                        <div className="w-3 h-3 bg-success-primary rounded-full"></div>
                        <span className="text-authority-dark font-medium">Available 24/7</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <div className="w-3 h-3 bg-success-primary rounded-full"></div>
                        <span className="text-authority-dark font-medium">Licensed & Insured</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <div className="w-3 h-3 bg-success-primary rounded-full"></div>
                        <span className="text-authority-dark font-medium">Free Estimates</span>
                      </div>
                    </div>
                  </div>

                  {/* Emergency Service Info */}
                  <div className="bg-gradient-to-r from-action-urgent to-red-700 rounded-xl p-6 text-white shadow-xl border-l-4 border-red-600">
                    <div className="flex items-center justify-center lg:justify-start mb-3">
                      <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center mr-3">
                        <Clock size={20} className="text-white" />
                      </div>
                      <h4 className="font-bold text-lg">🚨 24/7 Emergency Service</h4>
                    </div>
                    <p className="text-red-100 text-center lg:text-left">
                      Emergency plumbing, burst pipes, sewer backups? We respond to Thornton emergencies within 2 hours.
                    </p>
                  </div>
                </div>
              </div>

              {/* Thornton Service Areas */}
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-center lg:text-left text-authority-navy">Thornton Service Areas</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-neutral-white rounded-lg p-4 shadow-lg border-l-4 border-trust-deep hover:shadow-xl transition-all duration-300">
                    <div className="w-6 h-6 bg-gradient-to-r from-trust-deep to-authority-navy rounded-full flex items-center justify-center mb-2">
                      <MapPin size={16} className="text-white" />
                    </div>
                    <h4 className="font-semibold mb-1 text-authority-navy">Central Thornton</h4>
                    <ul className="text-sm text-neutral-gray space-y-1">
                      <li>• Downtown Thornton</li>
                      <li>• Thornton City Center</li>
                      <li>• Grant Ranch</li>
                    </ul>
                  </div>
                  
                  <div className="bg-neutral-white rounded-lg p-4 shadow-lg border-l-4 border-success-primary hover:shadow-xl transition-all duration-300">
                    <div className="w-6 h-6 bg-gradient-to-r from-success-primary to-success-eco rounded-full flex items-center justify-center mb-2">
                      <MapPin size={16} className="text-white" />
                    </div>
                    <h4 className="font-semibold mb-1 text-authority-navy">North Thornton</h4>
                    <ul className="text-sm text-neutral-gray space-y-1">
                      <li>• Northglenn border</li>
                      <li>• Legacy Ridge</li>
                      <li>• Eastlake</li>
                    </ul>
                  </div>
                  
                  <div className="bg-neutral-white rounded-lg p-4 shadow-lg border-l-4 border-trust-primary hover:shadow-xl transition-all duration-300">
                    <div className="w-6 h-6 bg-gradient-to-r from-trust-primary to-trust-deep rounded-full flex items-center justify-center mb-2">
                      <MapPin size={16} className="text-white" />
                    </div>
                    <h4 className="font-semibold mb-1 text-authority-navy">South Thornton</h4>
                    <ul className="text-sm text-neutral-gray space-y-1">
                      <li>• Thornton Pkwy</li>
                      <li>• Riverdale</li>
                      <li>• Highland</li>
                    </ul>
                  </div>
                  
                  <div className="bg-neutral-white rounded-lg p-4 shadow-lg border-l-4 border-action-warm hover:shadow-xl transition-all duration-300">
                    <div className="w-6 h-6 bg-gradient-to-r from-action-warm to-action-primary rounded-full flex items-center justify-center mb-2">
                      <MapPin size={16} className="text-white" />
                    </div>
                    <h4 className="font-semibold mb-1 text-authority-navy">West Thornton</h4>
                    <ul className="text-sm text-neutral-gray space-y-1">
                      <li>• Thornton Station</li>
                      <li>• Stonegate</li>
                      <li>• Thorncreek</li>
                    </ul>
                  </div>
                </div>

                {/* Service Coverage */}
                <div className="bg-gradient-to-r from-success-light to-success-primary/10 rounded-xl p-4 border-2 border-success-primary/20">
                  <div className="flex items-center justify-center lg:justify-start mb-2">
                    <div className="w-6 h-6 bg-success-primary rounded-full flex items-center justify-center mr-3">
                      <MapPin size={16} className="text-white" />
                    </div>
                    <h4 className="font-bold text-success-primary">✅ Complete Thornton Coverage</h4>
                  </div>
                  <p className="text-success-primary/80 text-center lg:text-left text-sm font-medium">
                    Serving all Thornton zip codes and surrounding Adams County. Professional plumbing and emergency services.
                  </p>
                </div>
              </div>
            </div>

            {/* Final CTA Section */}
            <div className="mt-12 text-center">
              <div className="bg-gradient-to-r from-trust-deep to-authority-navy rounded-2xl p-8 shadow-2xl border border-trust-primary/20">
                <h3 className="text-2xl font-bold mb-4 text-neutral-white">Why Choose Colorado First Plumbing for Your Thornton Property?</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 bg-gradient-to-r from-action-primary to-action-urgent rounded-full flex items-center justify-center mb-3 shadow-lg">
                      <Clock size={24} className="text-white" />
                    </div>
                    <h4 className="font-bold text-neutral-white mb-2">🚨 24/7 Emergency Service</h4>
                    <p className="text-sm text-trust-light">2-hour response time in Thornton</p>
                  </div>
                  
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 bg-gradient-to-r from-success-primary to-success-eco rounded-full flex items-center justify-center mb-3 shadow-lg">
                      <Award size={24} className="text-white" />
                    </div>
                    <h4 className="font-bold text-neutral-white mb-2">⭐ 300+ Sewer Projects</h4>
                    <p className="text-sm text-trust-light">More documented experience than competitors</p>
                  </div>
                  
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 bg-gradient-to-r from-trust-primary to-trust-deep rounded-full flex items-center justify-center mb-3 shadow-lg">
                      <Phone size={24} className="text-white" />
                    </div>
                    <h4 className="font-bold text-neutral-white mb-2">💰 Transparent Pricing</h4>
                    <p className="text-sm text-trust-light">Only contractor showing actual prices online</p>
                  </div>
                </div>

                <div className="space-y-4 md:space-y-0 md:space-x-4 md:flex md:justify-center">
                  
                  <a 
                    href="/pricing" 
                    className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-8 py-4 rounded-full text-xl font-semibold inline-block transition-all duration-300 w-full md:w-auto"
                  >
                    💰 View Pricing
                  </a>
                </div>
                
                <p className="text-trust-light mt-4 text-sm">
                  Serving Thornton since 2018 • Licensed Master Plumber • BBB A+ Rating • 4.8★ Reviews
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ThorntonPlumber;